import { useState, useRef, useEffect, memo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { findIndex, filter } from 'lodash'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { MapContainer, TileLayer, Polyline } from 'react-leaflet'
import {
  useRestricted,
  useSessionWS,
  useTrkptsWS,
  useLang,
  useUser,
} from '../utils/hooks'
import ChangeView from '../components/ChangeView'
import MovingMarker from '../components/MovingMarker'
import MoveSlider from '../components/MoveSlider'
import Help from '../components/Help'
import HelpNotif from '../components/HelpNotif'
import { getChartData, getMapData } from '../assets/assets_functions.js'
import { langPath } from '../assets/langPath'
import LineDraw from '../components/LineDraw'
import { RiRulerLine } from 'react-icons/ri'

import {
  multiPolylineFromArray,
  kStart,
  kPause,
  kStop,
} from '../assets/assets_functions'
import Footer from '../components/Footer.jsx'

const SessionWSTracks = ({ coords }) => {
  const chart = useRef()
  const firstDiv = useRef()

  const navigateTo = useNavigate()
  const { state } = useLocation()

  const { center, polygon } = state ? state : coords

  const { user } = useUser()
  const { restricted } = useRestricted()
  const { sessionWS } = useSessionWS()
  const { trkptsWS } = useTrkptsWS()

  const { lang } = useLang()
  const choosenLang = lang === 'fr' ? langPath.fr : langPath.eng

  const [chartOptions, setChartOptions] = useState(null)
  const [mapPath, setMapPath] = useState(null)
  const [restrTrkpts, setRestrTrkpts] = useState(null)

  const [showHelp, setShowHelp] = useState(false)

  const [measureDist, setMeasureDist] = useState(false)

  let map_zoom = 18

  useEffect(() => {
    if (!sessionWS) {
      const localUserId = localStorage.getItem('wsurf_userId')
      if (!localUserId || Number(localUserId) === -1) navigateTo('/login')
      else navigateTo('/sessions/')
    }
  }, [])

  useEffect(() => {
    if (firstDiv.current !== null) {
      const topScroll = firstDiv.current.getBoundingClientRect().top
      window.scrollTo({ top: topScroll.current - 120, behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    if (!sessionWS) return
    const { chartOptions } = getChartData(
      trkptsWS.brut,
      sessionWS.timeZone,
      restricted,
      'tracks',
      null,
    )
    const { mapPath } = getMapData(trkptsWS.brut, restricted)
    setRestrTrkpts(filter(trkptsWS.brut, (trkpt) => trkpt.excluded === 0))
    setChartOptions(chartOptions)
    setMapPath(mapPath)
  }, [])

  const base_colors = [
    '#f44336' /*red*/,
    '#4CAF50' /*blue*/,
    '#FFFF00' /*yellow*/,
    '#00FF00' /*green*/,
    '#00FFFF' /*skyblue*/,
    '#FF00FF' /*violet*/,
  ]

  const [move, setMove] = useState(kStop)
  const [moveSpeed, setMoveSpeed] = useState(20)
  const [markerPos, setMarkerPos] = useState(1)

  useEffect(() => {
    if (!sessionWS) return
    if (!chart.current) return

    Highcharts.addEvent(chart.current.chart, 'click', function (event) {
      const temp_pos = Math.round(event.xAxis[0].value / 1000) * 1000
      const temp_index = findIndex(restrTrkpts, (pos) => {
        return pos.timeU === temp_pos
      })
      if (temp_index !== -1) setMarkerPos(temp_index)
    })
  })

  let pathPolylines = []

  const tempMultiPolyline = multiPolylineFromArray(
    restricted
      ? trkptsWS.brut.filter((trkpt) => trkpt.excluded === 0)
      : trkptsWS.brut,
    base_colors[0],
  )

  if (tempMultiPolyline.multi !== null) {
    tempMultiPolyline.multi.map((point, index) => {
      pathPolylines.push(
        <Polyline
          pathOptions={point.pathOptions}
          positions={point.positions}
          key={index}
        />,
      )
      return null
    })
    //map_zoom = 16;
  }

  const startMoving = () => {
    setMove(kStart)
  }

  const pauseMoving = () => {
    setMove(kPause)
  }

  const stopMoving = () => {
    setMove(kStop)
    setMarkerPos(1)
  }

  const handleMoveEnded = () => {
    setMove(kStop)
    setMarkerPos(1)
  }

  const handleMoveSliderChange = (nb) => {
    setMoveSpeed(nb)
  }

  const handlePosChanged = (pos) => {
    if (pos < 6) return
    if (pos % 6 !== 0) return
    const temp_chart = chart.current.chart
    //temp_chart.xAxis[0].drawCrosshair(null, temp_chart.series[0].points[pos])
    //temp_chart.series[0].points[pos - 3].select(false, true)
    temp_chart.series[0].points[pos].select(true, false)
    /* chart.current.chart.tooltip.refresh(
      chart.current.chart.series[0].points[pos]
    ) */
  }

  const handleHelp = (what) => {
    setShowHelp(what)
  }

  const handleMeasureDist = () => {
    setMeasureDist(!measureDist)
  }

  const handleToSessionStats = () => {
    navigateTo('/oneSessionStats/')
  }

  return (
    <div ref={firstDiv}>
      <section
        className={
          'section ' +
          (user.userId !== -1 ? 'pt-0 pl-0 pb-0' : 'pt-4 pl-6 pb-0')
        }
      >
        <div className="level">
          <div className="level-left"></div>
          <div className="level-center"></div>
          <div className="level-right">
            <div className="level-item mr-0">
              {!showHelp && <Help handleHelp={handleHelp} />}
            </div>
            <div className="level-item">
              {user.userId === -1 && (
                <div className="mr-6 mb-2" style={{ textAlign: 'end' }}>
                  <button
                    className="button is-primary"
                    onClick={handleToSessionStats}
                  >
                    {choosenLang.backToStats}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {showHelp && (
          <HelpNotif handleHelp={handleHelp} helpText={choosenLang.help} />
        )}
        {mapPath && (
          <div>
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <button
                    className="button is-small"
                    style={{ color: 'blue' }}
                    onClick={startMoving}
                  >
                    <span className="icon">
                      <i className="fa fa-play"></i>
                    </span>
                  </button>
                  <button
                    className="button is-small"
                    style={{ color: 'blue' }}
                    onClick={pauseMoving}
                  >
                    <span className="icon">
                      <i className="fa fa-pause"></i>
                    </span>
                  </button>
                  <button
                    className="button is-small"
                    style={{ color: 'blue' }}
                    onClick={stopMoving}
                  >
                    <span className="icon">
                      <i className="fa fa-stop"></i>
                    </span>
                  </button>
                </div>

                <MoveSlider
                  move_speed={moveSpeed}
                  handle_move_slider_change={handleMoveSliderChange}
                />
              </div>
            </div>

            <div style={{ width: '100%' }}>
              <MapContainer scrollWheelZoom={true} maxZoom={18} zoomOffset={-1}>
                <ChangeView
                  center={center}
                  origin="tracks"
                  map_bounds={polygon}
                  map_path={mapPath.positions}
                />
                <TileLayer
                  attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  zIndex="0"
                />
                {pathPolylines}
                {move !== kStop && (
                  <MovingMarker
                    currentPos={markerPos}
                    movePos={tempMultiPolyline.pos_a}
                    move={move}
                    moveSpeed={moveSpeed}
                    handleMoveEnded={handleMoveEnded}
                    handlePosChanged={handlePosChanged}
                  />
                )}
                {measureDist && <LineDraw />}
                <div
                  style={{
                    zIndex: '800',
                    position: 'relative',
                    top: '100px',
                    left: '10px',
                  }}
                >
                  <button
                    className="button"
                    onClick={() => handleMeasureDist()}
                    title={choosenLang.measureBtn}
                  >
                    <span
                      className={
                        'icon is-large ' +
                        (measureDist === true
                          ? 'has-text-danger'
                          : 'has-text-primary')
                      }
                    >
                      <RiRulerLine />
                    </span>
                  </button>
                </div>
              </MapContainer>
            </div>
          </div>
        )}
      </section>
      {chartOptions && (
        <section className="pl-0 pr-6">
          <HighchartsReact
            ref={chart}
            highcharts={Highcharts}
            options={chartOptions}
            style={{ zindex: '50000' }}
          />
        </section>
      )}
      {user.userId === -1 && <Footer />}
    </div>
  )
}

export default memo(SessionWSTracks)
